/**
 * NotificationCentre — F 490's bell, unread badge, and inbox.
 *
 * WHAT IT IS
 * ----------
 * A bell in the header that carries an unread count, and an inbox panel
 * that lists the notifications. Clicking a notification marks it read, and
 * the bell's count falls.
 *
 * WHY THE BELL REPLACED AN ALERT
 * ------------------------------
 * The header ALREADY had a bell, and it was wired to
 * `alert('No notifications')`. So the bell's SHAPE is already proven in
 * production; what was missing was the behaviour. This component replaces
 * the alert with a real feed, and keeps the bell exactly where it was so
 * the layout does not move.
 *
 * WHY THE COUNT IS THE UNREAD COUNT AND NOT THE TOTAL
 * ----------------------------------------------------
 * A badge that counts everything tells the operator nothing they can act
 * on. The badge counts what has NOT been read, so it is zero when there is
 * nothing to do, and it is the number that goes down when they act.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { ActionIcon, Badge, Box, Button, Divider, Group, Popover, ScrollArea, Stack, Text } from '@mantine/core';
import { IconBell, IconBellOff, IconCheck } from '@tabler/icons-react';
import type { Notification } from '../../../types/models/Reminder';
import type { ReminderStore } from '../../../core/reminder/store';
import { useNotifications } from '../../hooks/useNotifications';
import './NotificationCentre.css';

export interface NotificationCentreProps {
  /** The store the notifications are read through. */
  store: ReminderStore;
  /** Called when a notification is clicked — used to navigate to its node. */
  onOpenNotification?: (n: Notification) => void;
  className?: string;
}

/** `2:45 PM` — a short, human time. */
function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** `Oct 26, 2:45 PM` — a fuller time, for the inbox rows. */
function formatFull(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

export const NotificationCentre: React.FC<NotificationCentreProps> = ({
  store,
  onOpenNotification,
  className = '',
}) => {
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications(store);
  const [opened, setOpened] = useState(false);

  const handleClick = useCallback(async (n: Notification) => {
    // Mark it read FIRST, so the badge falls even if the navigation throws.
    await markRead(n.id);
    onOpenNotification?.(n);
  }, [markRead, onOpenNotification]);

  const rows = useMemo(
    () => notifications.slice(0, 50),
    [notifications],
  );

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      shadow="md"
      width={340}
      withinPortal
      /**
       * The same z-index reasoning as `SetReminder`: Mantine's Popover
       * defaults to 300, and the app's panels are at 1100+. A bell in the
       * header whose inbox renders behind a panel is an inbox nobody can
       * read. 3200 — above every panel, below the 9999 emergency layer.
       *
       * AND `closeOnClickOutside={false}`, for the same reason as the
       * reminder popover: Mantine's Popover closes on `mousedown`, and a
       * mousedown on an inbox row removed the row before its `click` could
       * reach it. The instrumented log was unambiguous — `pointerdown`,
       * `mousedown`, and no `click` at all — so clicking a notification
       * with a mouse would have marked NOTHING read.
       */
      zIndex={3200}
      closeOnClickOutside={false}
    >
      <Popover.Target>
        <Box className={`fc-notification-centre ${className}`} style={{ position: 'relative' }}>
          <ActionIcon
            className="fc-header-icon fc-header-hide-narrow"
            variant="subtle"
            size="md"
            title="Notifications"
            aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}
            data-testid="fc-notification-bell"
            onClick={() => setOpened((o) => !o)}
          >
            <IconBell size={18} />
          </ActionIcon>
          {unreadCount > 0 && (
            <Badge
              className="fc-notification-centre__badge"
              size="xs"
              circle
              variant="filled"
              color="red"
              data-testid="fc-notification-badge"
            >
              {unreadCount}
            </Badge>
          )}
        </Box>
      </Popover.Target>

      <Popover.Dropdown data-testid="fc-notification-inbox">
        <Group justify="space-between" mb="xs">
          <Text size="sm" fw={600}>Notifications</Text>
          {unreadCount > 0 && (
            <Button
              size="compact-xs"
              variant="subtle"
              leftSection={<IconCheck size={12} />}
              onClick={() => void markAllRead()}
              data-testid="fc-notification-mark-all-read"
            >
              Mark all read
            </Button>
          )}
        </Group>
        <Divider mb="xs" />

        {rows.length === 0 ? (
          <Stack align="center" gap={4} py="md">
            <IconBellOff size={20} color="var(--fc-text-hint, #9b9a97)" />
            <Text size="xs" c="dimmed">No notifications yet</Text>
          </Stack>
        ) : (
          <ScrollArea.Autosize mah={320}>
            <Stack gap={2}>
              {rows.map((n) => (
                <Box
                  key={n.id}
                  className={`fc-notification-centre__row${n.read ? '' : ' fc-notification-centre__row--unread'}`}
                  data-testid="fc-notification-item"
                  data-notification-id={n.id}
                  data-read={String(n.read)}
                  onClick={() => void handleClick(n)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') void handleClick(n); }}
                  role="button"
                  tabIndex={0}
                >
                  <Group gap={8} wrap="nowrap" align="flex-start">
                    {!n.read && <span className="fc-notification-centre__dot" aria-hidden="true" />}
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Text size="xs" fw={n.read ? 400 : 600} truncate>{n.text}</Text>
                      <Text size="10px" c="dimmed">{formatFull(n.fired_at)}</Text>
                    </Box>
                    {!n.read && (
                      <Text size="10px" c="dimmed" data-testid="fc-notification-time">
                        {formatTime(n.fired_at)}
                      </Text>
                    )}
                  </Group>
                </Box>
              ))}
            </Stack>
          </ScrollArea.Autosize>
        )}
      </Popover.Dropdown>
    </Popover>
  );
};
